import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Eye, EyeOff } from 'lucide-react';

export function ShotGroupTable({ shotGroup, onToggleShot, onSelectShot, selectedShots = [], showAverage = true }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = (metricKey) => {
    if (sortKey === metricKey) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(metricKey);
      setSortDirection('asc');
    }
  };

  const sortedShots = sortKey
    ? [...shotGroup.shots].sort((a, b) => {
        const aVal = sortKey === 'shotNumber' ? a.shotNumber : (a.metrics[sortKey] ?? -Infinity);
        const bVal = sortKey === 'shotNumber' ? b.shotNumber : (b.metrics[sortKey] ?? -Infinity);
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      })
    : shotGroup.shots;

  // Calculate averages and consistency (std dev) from included shots only
  const includedShots = shotGroup.shots.filter(shot => shot.included);

  const calculateAverage = (metricKey) => {
    if (includedShots.length === 0) return 0;
    const sum = includedShots.reduce((acc, shot) => acc + (shot.metrics[metricKey] || 0), 0);
    return sum / includedShots.length;
  };

  const calculateStdDev = (metricKey) => {
    if (includedShots.length === 0) return 0;
    const avg = calculateAverage(metricKey);
    const squaredDiffs = includedShots.map(shot =>
      Math.pow((shot.metrics[metricKey] || 0) - avg, 2)
    );
    const variance = squaredDiffs.reduce((a, b) => a + b, 0) / includedShots.length;
    return Math.sqrt(variance);
  };

  const metrics = [
    { key: 'clubSpeed', label: 'Club Speed', unit: 'mph', decimals: 1 },
    { key: 'ballSpeed', label: 'Ball Speed', unit: 'mph', decimals: 1 },
    { key: 'smashFactor', label: 'Smash Fac.', unit: '', decimals: 2 },
    { key: 'launchAngle', label: 'Launch Ang.', unit: '°', decimals: 1 },
    { key: 'spinRate', label: 'Spin Rate', unit: 'rpm', decimals: 0 },
    { key: 'spinAxis', label: 'Spin Axis', unit: '°', decimals: 1 },
    { key: 'sideSpin', label: 'Side Spin', unit: 'rpm', decimals: 0 },
    { key: 'carry', label: 'Carry', unit: 'Yds', decimals: 1 },
    { key: 'total', label: 'Total', unit: 'Yds', decimals: 1 },
    { key: 'landingAngle', label: 'Land. Ang.', unit: '°', decimals: 1 },
    { key: 'height', label: 'Height', unit: 'Ft', decimals: 0 },
    { key: 'attackAngle', label: 'Attack Ang.', unit: '°', decimals: 1 },
    { key: 'offlineDistance', label: 'Offline', unit: 'Yds', decimals: 1 },
    { key: 'clubPath', label: 'Club Path', unit: '°', decimals: 1 },
    { key: 'faceAngle', label: 'Face Angle', unit: '°', decimals: 1 },
    { key: 'dynamicLoft', label: 'Dyn. Loft', unit: '°', decimals: 1 },
  ];

  return (
    <div className="bg-surface rounded-lg border border-border overflow-hidden mb-4">
      {/* Header */}
      <div className="bg-surface-light px-4 py-3 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: shotGroup.color }}
          />
          <h3 className="font-semibold text-text-primary">
            {shotGroup.name}
          </h3>
          {shotGroup.isBaseline && (
            <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded">
              Baseline
            </span>
          )}
          <span className="text-sm text-text-muted">
            ({includedShots.length}/{shotGroup.shots.length} shots)
          </span>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary transition-colors"
        >
          {isExpanded ? (
            <>
              <span>Hide</span>
              <ChevronUp size={16} />
            </>
          ) : (
            <>
              <span>Show</span>
              <ChevronDown size={16} />
            </>
          )}
        </button>
      </div>

      {/* Table */}
      {isExpanded && (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-surface-light border-b border-border">
                <th
                  onClick={() => handleSort('shotNumber')}
                  className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase tracking-wider sticky left-0 bg-surface-light z-10 cursor-pointer hover:text-text-primary select-none"
                >
                  Shot {sortKey === 'shotNumber' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-text-muted uppercase tracking-wider sticky left-[60px] bg-surface-light z-10">

                </th>
                {metrics.map(metric => (
                  <th
                    key={metric.key}
                    onClick={() => handleSort(metric.key)}
                    className="px-3 py-2 text-right text-xs font-medium text-text-muted uppercase tracking-wider whitespace-nowrap cursor-pointer hover:text-text-primary select-none"
                  >
                    {metric.label}
                    {metric.unit && <span className="ml-1 text-text-muted">({metric.unit})</span>}
                    {sortKey === metric.key && (
                      <span className="ml-1 text-primary">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Shot rows */}
              {sortedShots.map(shot => {
                const isSelected = selectedShots.some(s => s.groupId === shotGroup.id && s.shotNumber === shot.shotNumber);
                return (
                  <tr
                    key={shot.shotNumber}
                    onClick={() => onSelectShot && onSelectShot(shotGroup.id, shot.shotNumber)}
                    className={`border-b border-border/50 hover:bg-surface-light transition-colors cursor-pointer ${
                      !shot.included ? 'bg-surface-light' : ''
                    } ${isSelected ? 'ring-2 ring-primary bg-primary/10' : ''}`}
                  >
                    <td className={`px-3 py-2 text-sm font-medium sticky left-0 z-10 ${
                      !shot.included ? 'text-text-muted line-through bg-surface-light' : isSelected ? 'text-primary font-bold bg-primary/10' : 'text-text-primary bg-surface'
                    }`}>
                      #{shot.shotNumber}
                    </td>
                    <td className={`px-3 py-2 text-center sticky left-[60px] z-10 ${
                      !shot.included ? 'bg-surface-light' : isSelected ? 'bg-primary/10' : 'bg-surface'
                    }`}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggleShot(shotGroup.id, shot.shotNumber);
                        }}
                        className="text-text-muted hover:text-text-secondary transition-colors"
                        title={shot.included ? 'Exclude shot' : 'Include shot'}
                      >
                        {shot.included ? <Eye size={16} /> : <EyeOff size={16} />}
                      </button>
                    </td>
                    {metrics.map(metric => (
                      <td
                        key={metric.key}
                        className={`px-3 py-2 text-right text-sm font-mono ${
                          !shot.included ? 'text-text-muted line-through' : isSelected ? 'text-primary font-semibold' : 'text-text-primary'
                        }`}
                        style={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {shot.metrics[metric.key] != null
                          ? shot.metrics[metric.key].toFixed(metric.decimals)
                          : '--'
                        }
                      </td>
                    ))}
                  </tr>
                );
              })}

              {/* Average row */}
              {showAverage && (
                <>
                  <tr className="bg-primary/20 border-t-2 border-primary">
                    <td className="px-3 py-2 text-sm font-bold text-primary sticky left-0 bg-primary/20 z-10">
                      Average
                    </td>
                    <td className="sticky left-[60px] bg-primary/20 z-10"></td>
                    {metrics.map(metric => (
                      <td
                        key={metric.key}
                        className="px-3 py-2 text-right text-sm font-mono font-semibold text-primary"
                        style={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {calculateAverage(metric.key).toFixed(metric.decimals)}
                      </td>
                    ))}
                  </tr>

                  {/* Consistency (Std Dev) row */}
                  <tr className="bg-secondary/20">
                    <td className="px-3 py-2 text-sm font-bold text-secondary sticky left-0 bg-secondary/20 z-10">
                      Consistency
                    </td>
                    <td className="px-3 py-2 sticky left-[60px] bg-secondary/20 z-10">
                      <span className="text-xs text-secondary">(σ)</span>
                    </td>
                    {metrics.map(metric => (
                      <td
                        key={metric.key}
                        className="px-3 py-2 text-right text-sm font-mono font-semibold text-secondary"
                        style={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {calculateStdDev(metric.key).toFixed(metric.decimals)}
                      </td>
                    ))}
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
